import { standardResolutions } from "./constants";
import { calculateStatistics } from "../../utils/statistics";
import { resolvePromises } from "../../utils/promise";

/**
 * The delay (in milliseconds) after stopping the video stream.
 * 1s was selected due to certain device and browser combinations being problematic,
 * such as Firefox on Samsung phones, and some emulators.
 */
const STOP_VIDEO_STREAM_DELAY = 1_000;

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} ms The number of milliseconds to sleep.
 * @returns {Promise<void>} A promise that resolves after the specified duration.
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Stops the provided video stream by stopping all its tracks. If `delay` is
 * provided and greater than 0 (`delay > 0`), the execution is paused by `delay`
 * milliseconds.
 *
 * @param {MediaStream} stream The video stream to be stopped.
 * @param {number} delay The number of milliseconds to stop the execution for.
 */
const stopVideoStream = async (stream, delay = 0) => {
  stream.getTracks().forEach((track) => track.stop());
  if (delay > 0) await sleep(delay);
};

/**
 * Returns an array with the available data of the video devices.
 * @param {HTMLVideoElement} video The video element in use.
 * @param {Array.<string> | undefined} cameraIds An array with the ids of the camera to be
 * analyzed. If not provided, then all the cameras will be analyzed.
 * @returns An array with the available data of the video devices.
 */
export const getAllCamsData = async (video, cameraIds) => {
  await requestCameraPermissions();
  if (!cameraIds) {
    cameraIds = (await getAllCams()).map((device) => device.deviceId);
  }
  const cameraDataPromises = cameraIds.map((id) => async () => {
    const camData = await getCamData(id);
    const resolutions = await getCamResolutions(id);
    const fps = await getFpsData(id, video);
    const startupBehavior = await getCamStartupData(id);
    if (camData) {
      return { ...camData, resolutions, startupBehavior, fps };
    }
  });
  return await resolvePromises(cameraDataPromises);
};

/** Requests for the permissions for all the video devices. */
const requestCameraPermissions = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    await stopVideoStream(stream);
  } catch (err) {
    console.error(err);
  }
};

/** Returns an array with the ID of all the video devices */
const getAllCams = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter(({ kind }) => kind === "videoinput");
};

/** Returns the data of the camera with the given id. If there is no camera available,
 *  returns `undefined`.*/
const getCamData = async (deviceId) => {
  if (!deviceId) return;

  // Get the video streams
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
    });
  } catch (err) {
    console.error(err);
  }
  if (!stream) return;

  const streamTrack = stream.getVideoTracks()[0];

  const supportGetCapabilities =
    typeof InstallTrigger === "undefined" && !document.documentMode;

  const camData = streamTrack
    ? {
        streamTrack: {
          streamTrack: streamTrack,
          enabled: streamTrack.enabled,
          id: streamTrack.id,
          kind: streamTrack.kind,
          label: streamTrack.label,
          muted: streamTrack.muted,
          readyState: streamTrack.readyState,
          remote: streamTrack.remote,
        },
        settings: streamTrack.getSettings(),
        capabilities: supportGetCapabilities
          ? streamTrack.getCapabilities()
          : "The browser does not support `streamTrack.getCapabilities()`",
        constraints: streamTrack.getConstraints(),
      }
    : undefined;
  streamTrack.stop();
  await stopVideoStream(stream);
  return camData;
};

const getCamResolutions = async (deviceId) => {
  let stream = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
    });
  } catch (err) {
    console.error(err);
  }
  if (!stream) return;

  const tracks = stream.getVideoTracks();

  const resolutionsPromises = standardResolutions.map(
    (resolution) => async () => {
      const videoConstraints = {
        deviceId: { exact: deviceId },
        width: {
          exact: resolution.width,
        },
        height: {
          exact: resolution.height,
        },
        resizeMode: {
          exact: "none",
        },
      };

      const tracksPromises = tracks.map((track) => async () => {
        let valid = true;
        const start = performance.now();
        try {
          await track.applyConstraints(videoConstraints);
        } catch (e) {
          valid = false;
        }
        const end = performance.now();
        return { valid, start, end };
      });
      const res = await resolvePromises(tracksPromises);

      const valid = res[res.length - 1].valid;
      const time = res[res.length - 1].end - res[res.length - 1].start;
      return { resolution, valid, time };
    }
  );
  const cameraResolutions = await resolvePromises(resolutionsPromises);

  if (stream) {
    await stopVideoStream(stream);
  }

  return cameraResolutions;
};

const getCamStartupData = async (deviceId) => {
  const startupPromises = [...Array(20).keys()].map(() => async () => {
    try {
      const start = performance.now();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });
      const end = performance.now();
      // Stop current camera stream and wait 1s so Firefox properly releases the
      // camera. Without this delay, Firefox (especially on mobile) may keep the
      // camera busy, causing the next getUserMedia() call to fail.
      // The delay is added to this data collection function and not the others
      // (getCamData, getCamResolutions...) because this is the last data
      // collection function in getAllCamsData, meaning that another camera
      // stream may be accessed after this one.
      await stopVideoStream(stream, STOP_VIDEO_STREAM_DELAY);
      return end - start;
    } catch (e) {
      console.log(`Not supported: ${e}`);
    }
  });
  const startupTimes = (await resolvePromises(startupPromises)).filter(
    (time) => typeof time === "number"
  );

  const { mean, variance } = calculateStatistics(startupTimes);
  const standardDeviation = Math.sqrt(variance);

  return { startupTimes, mean, standardDeviation };
};

/**
 * The maximum number of recent timestamp values the system will store in
 * memory per resolution.
 *
 * This value will determine the number of frames used to calculate the value of
 * the current FPS. Higher values will make the measure more stable but less
 * likely to detect abrupt FPS changes in the stream; whereas lower values will
 * make the measure less stable but more likely to detect abrupt FPS changes in
 * the stream.
 */
const MAX_NUMBER_TS_STORED = 30;
/**
 * The number of FPS in the list returned by `getFpsData`.
 */
const NUM_FPS_STORED = 120;

/**
 * Returns fps data about the device with the given id. To calculate the FPS
 * live, a video instance must be provided, and will be used to run different
 * streams on it and determine the frame rate.
 *
 * @param {string} deviceId The device id of the device to analyze.
 * @param {Object} video A video instance that will be used to run different
 * streams and analyze their frame rates.
 * @returns {Object} Data about the FPS performance of the device with the given
 * ID running in different resolutions.
 */
const getFpsData = async (deviceId, video) => {
  // If the browser does not support the check, then skip it.
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement#browser_compatibility
  if (!video.requestVideoFrameCallback) {
    return [];
  }
  if (!video) {
    throw new Error("No video reference provided for FPS analysis.");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: { deviceId: { exact: deviceId } },
  });
  video.srcObject = stream;

  const tracks = stream.getVideoTracks();

  const resolutionPromises = standardResolutions.map(
    (resolution) => async () => {
      const videoConstraints = {
        deviceId: { exact: deviceId },
        width: {
          exact: resolution.width,
        },
        height: {
          exact: resolution.height,
        },
        resizeMode: {
          exact: "none",
        },
      };

      const tracksPromises = tracks.map((track) => async () => {
        await track.applyConstraints(videoConstraints);

        const resolutionFpsData = await getResolutionFpsData(video);

        return {
          resolution,
          ...resolutionFpsData,
        };
      });
      return await resolvePromises(tracksPromises);
    }
  );
  const fpsData = (await resolvePromises(resolutionPromises))
    .flat(1)
    // We cannot use OverconstrainedError because at Firefox that error class is not exposed globally.
    .filter((data) => !(data && data.name === "OverconstrainedError"));

  await stopVideoStream(stream);
  video.srcObject = undefined;

  return fpsData;
};

const getResolutionFpsData = (currentVideoRef) => {
  return new Promise((resolve) => {
    const timestampsList = [];
    const fpsList = [];
    const update = (now) => {
      timestampsList.push(now);

      if (timestampsList.length >= MAX_NUMBER_TS_STORED) {
        timestampsList.shift();
      }

      const differences = timestampsList
        .map((_, i) => {
          return 1000 / (timestampsList[i] - timestampsList[i - 1]);
        })
        .filter(Boolean);
      const fps =
        differences.reduce((acc, current) => acc + current, 0) /
        differences.length;

      fpsList.push(fps);

      if (fpsList.length >= NUM_FPS_STORED + 1) {
        // Remove the first element because it is `null`.
        fpsList.shift();
        const fpsData = {
          fps: fpsList,
          length: fpsList.length,
          ...calculateStatistics(fpsList),
        };
        resolve(fpsData);
        return;
      }
      currentVideoRef.requestVideoFrameCallback(update);
    };
    currentVideoRef.requestVideoFrameCallback(update);
  });
};

/**
 * Returns a list with the labels and the ids of all the cameras.
 */
export const getBasicCameraData = async () => {
  await requestCameraPermissions();
  const cameras = await getAllCams();
  return cameras.map((camera) => ({
    id: camera.deviceId,
    label: camera.label,
  }));
};
